/**
 * A person's Delete, end to end (#781): request, progress toast, outcome.
 *
 * Small items and small folders answer inline, as before. A big folder is
 * queued as a background job (202) and followed over the websocket
 * (`createDeleteTracker`); the toast turns into a real bar while the job
 * reports its phases. The row's "Deleting…" state comes from the server's
 * marker, so every tab and teammate sees it, not only this one.
 *
 * A job still running when the page reloads is picked up again: its id is
 * kept in sessionStorage and followed on the next mount.
 *
 * Only the person's own Delete actions go through here. Deletes that are one
 * step of something else (replace-on-conflict, removing a chain, undo) keep
 * `filesApi.deleteFile`: they need the delete DONE before their next step.
 */
import { useCallback, useEffect, useRef } from 'react'
import { useRealtime } from '@papan/realtime/client'
import { useToast, type ProgressController } from '@papan/core-ui/ui'
import { captureUndoContext, type UndoCapture } from '@papan/core-ui/ui/undoCapture'
import { pinnedBatch, type PinnedBatch } from '@papan/api-client'
import { useTranslation } from '@papan/i18n'
import { FOLDER_BEING_DELETED_CODE, RESERVED_NAME_CODE, FOLDER_TOO_LARGE_TO_DELETE_CODE } from '@papan/types'
import {
  filesApi,
  createDeleteTracker,
  pendingDeletes,
  requestOutcomeUnknown,
  JobFailedError,
  JobLostError,
  type DeleteJobOutcome,
  type DeleteJobProgress,
  type PendingDelete,
  type DeleteTracker,
} from '@papan/files/client'
import { partitionRefusalKey, deleteResultMessage } from './protectedBranchFlow'

export interface DeleteTarget {
  id: string
  name: string
  /** #801: the preview's removable ids, when the person confirmed a split. */
  expectRemovable?: string[]
}

export interface DeleteRunOptions {
  /** A delete finished (inline or as a job). `deletedInSource`: the bin entry
   *  is in another org's bin, so offer no undo. Also called for a job resumed
   *  after a reload. `at` (D134): the undo context captured when the delete's request was SENT — absent for a job
   *  resumed after a reload (sent by an earlier page, possibly as another identity: no undo is recorded for it). */
  onDeleted?: (target: DeleteTarget, outcome: DeleteJobOutcome, at?: UndoCapture) => void
  /** Every delete in a run has settled, whatever the outcome. */
  onSettled?: () => void
}

export type DeleteOutcome =
  | { ok: true; target: DeleteTarget; result: DeleteJobOutcome }
  | { ok: false; target: DeleteTarget; message: string }

/**
 * A write refused because its folder is being deleted (409 FOLDER_BEING_DELETED
 * — move, rename, copy, new folder, upload), in the person's language; null
 * for any other error, so callers fall back to their own message.
 */
export function folderBeingDeletedText(err: unknown, t: (key: string) => string): string | null {
  return (err as { code?: string } | null)?.code === FOLDER_BEING_DELETED_CODE
    ? t('driveView.toast.writeIntoDeletingFolder')
    : null
}

/**
 * Owner D145 — a name refused because it is RESERVED in the drive (400 RESERVED_NAME — a SQLite side-file name such as
 * `x.db-wal`; rename, new folder, upload, move, paste), in the person's language; null for any other error.
 */
export function reservedNameText(err: unknown, t: (key: string) => string): string | null {
  return (err as { code?: string } | null)?.code === RESERVED_NAME_CODE ? t('driveView.toast.reservedName') : null
}

/**
 * A paste refused because the existing item is an app's live database and the paste would have
 * moved it to the Bin (409 ENGINE_DB_OWNED — a folder or link pasted over a db's name), in the
 * person's language; null for any other error, so callers fall back to their own message. (Replace
 * of a file onto a db is a new version through the adopt — not this.)
 */
export function engineDbOwnedText(err: unknown, t: (key: string) => string): string | null {
  return (err as { code?: string } | null)?.code === 'ENGINE_DB_OWNED' ? t('driveView.toast.engineDbOwned') : null
}

/** #1290 — a database is busy (DB_BUSY: try again in a moment) or being replaced in the background
 *  (ENGINE_DB_REPLACING); null for any other error. */
export function engineDbBusyText(err: unknown, t: (key: string) => string): string | null {
  const code = (err as { code?: string } | null)?.code
  return code === 'DB_BUSY' ? t('driveView.toast.engineDbBusy') : code === 'ENGINE_DB_REPLACING' ? t('driveView.toast.engineDbReplacing') : null
}

/** Replace onto an app's live database with a file that is not a valid SQLite database (422
 *  ENGINE_DB_INVALID — the database was left unchanged); null for any other error. */
export function engineDbInvalidText(err: unknown, t: (key: string) => string): string | null {
  return (err as { code?: string } | null)?.code === 'ENGINE_DB_INVALID' ? t('driveView.toast.engineDbInvalid') : null
}

/** An extract stopped at an entry that would be an app's database but isn't valid SQLite (422 ENGINE_DB_INVALID) — what
 *  a Replace onto a live database is told differs (`engineDbInvalidText`); null for any other error. */
export function extractDbInvalidText(err: unknown, t: (key: string) => string): string | null {
  return (err as { code?: string } | null)?.code === 'ENGINE_DB_INVALID' ? t('driveView.toast.extractDbInvalid') : null
}

/** Jobs this tab is already following — two mounted views (keep-alive decks)
 *  must not both resume the same reloaded job. */
const following = new Set<string>()

function newOperationId(): string {
  return `delete-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

export function useDeleteRunner(options: DeleteRunOptions = {}) {
  const { t } = useTranslation('files')
  const toast = useToast()
  const { status, onOperationEvent } = useRealtime()
  const statusRef = useRef(status)
  statusRef.current = status
  const optsRef = useRef(options)
  optsRef.current = options

  /** `batch` (#1316 review round 2): a person-started delete — its job is followed as the identity that started it
   *  and to its end (`runsToCompletion`), whatever session the tab is in by then. */
  const deps = useCallback((batch?: PinnedBatch) => ({
    onOperationEvent,
    isConnected: () => statusRef.current === 'connected',
    getStatus: (jobId: string) => filesApi.getDeleteStatus(jobId, batch?.opts()),
    runsToCompletion: !!batch?.opts().identity,
  }), [onOperationEvent])

  /** A refusal or failure in the person's words. */
  const failureText = useCallback((err: unknown, name: string): string => {
    const code = (err as { code?: string })?.code
    // #801: refused whole, or the folder changed since the preview.
    const refusal = partitionRefusalKey(err, 'delete')
    if (refusal) return t(refusal)
    if (code === FOLDER_BEING_DELETED_CODE) return t('driveView.toast.folderBeingDeleted', { name })
    if (code === FOLDER_TOO_LARGE_TO_DELETE_CODE) return t('driveView.toast.folderTooLargeToDelete', { name })
    if (err instanceof JobLostError) return t('driveView.toast.deleteOutcomeUnknown', { name })
    // A job's own failure carries the server's plain-words reason.
    if (err instanceof JobFailedError && err.message) return err.message
    return t('driveView.toast.deleteFailed')
  }, [t])

  const phaseText = useCallback((p: DeleteJobProgress, name: string): string => {
    if (p.phase === 'snapshot' || p.phase === 'permissions') return t('driveView.toast.deletingCollecting', { name })
    if (p.phase === 'unlink') return t('driveView.toast.deletingRemoving', { name })
    return t('driveView.toast.deletingFinishing', { name })
  }, [t])

  /** Follow one queued job to its end, keeping the reload record in step. */
  const followJob = useCallback(async (
    pending: PendingDelete,
    tracker: DeleteTracker,
    onProgress?: (p: DeleteJobProgress) => void,
  ): Promise<DeleteJobOutcome> => {
    following.add(pending.jobId)
    pendingDeletes.add(pending)
    try {
      return await tracker.follow(pending.jobId, pending.operationId, onProgress)
    } finally {
      following.delete(pending.jobId)
      pendingDeletes.remove(pending.jobId)
    }
  }, [])

  /**
   * Send one Delete. Resolves when the SERVER has answered — with the finished
   * outcome for an inline delete, or with a promise of it for a queued job.
   */
  const startOne = useCallback(async (
    target: DeleteTarget,
    onProgress: ((p: DeleteJobProgress) => void) | undefined,
    batch: PinnedBatch,
  ): Promise<{ finished: Promise<DeleteOutcome> }> => {
    // Wrapped: an async function returning a bare promise would wait for it.
    const operationId = newOperationId()
    let tracker: ReturnType<typeof createDeleteTracker>
    try { tracker = createDeleteTracker(deps(batch), operationId) }
    catch (err) { return { finished: Promise.resolve({ ok: false, target, message: failureText(err, target.name) }) } }
    const send = () => filesApi.deleteFileAsync(
      target.id, operationId, { ...(target.expectRemovable ? { expectRemovable: target.expectRemovable } : {}), ...batch.opts() })
    try {
      let started: Awaited<ReturnType<typeof send>>
      try {
        started = await send()
      } catch (err) {
        if (!requestOutcomeUnknown(err)) throw err
        // No answer arrived, so the server may have taken the folder anyway
        // (seen on stagetest2: a 202 that took 30 s, after the tab had said
        // "Failed", then the row went back to "Deleting…"). Asking again is
        // safe: a second Delete by the same person joins the running job and
        // answers with its id; a delete that already finished answers 404.
        try {
          started = await send()
        } catch (retryErr) {
          if (requestOutcomeUnknown(retryErr) || (retryErr as { status?: number })?.status === 404) throw new JobLostError()
          throw retryErr
        }
      }
      if (started.kind === 'done') {
        tracker.dispose()
        const { kind: _kind, ...result } = started
        return { finished: Promise.resolve({ ok: true, target, result }) }
      }
      const pending: PendingDelete = { jobId: started.jobId, operationId: started.operationId, fileId: target.id, name: target.name, startedAt: Date.now() }
      return {
        finished: followJob(pending, tracker, onProgress).then(
          (result): DeleteOutcome => ({ ok: true, target, result }),
          (err): DeleteOutcome => ({ ok: false, target, message: failureText(err, target.name) }),
        ),
      }
    } catch (err) {
      tracker.dispose()
      return { finished: Promise.resolve({ ok: false, target, message: failureText(err, target.name) }) }
    }
  }, [deps, followJob, failureText])

  /**
   * Delete `targets` with one progress toast. One target: a bar driven by the
   * job's phases (indeterminate for an inline delete). Several: counts items.
   * The requests go out one at a time — the server judges each against the
   * tree the previous one left — but queued jobs then run side by side.
   */
  const deleteItems = useCallback(async (targets: DeleteTarget[]): Promise<DeleteOutcome[]> => {
    if (targets.length === 0) return []
    // #1316 review round 2 (owner, 2026-10-28): the delete the person started FINISHES as the identity that started it —
    // every request is sent as it (`batch.opts()`), a tenant / identity switch mid-way neither stops it nor sends the rest
    // as the next session; what it SHOWS (the progress, the result toasts, the refresh) stops with its session
    const batch = pinnedBatch()
    const at = captureUndoContext()   // D134: before any request — the identity the deletes are sent as
    const shown = () => !batch.ended()
    const single = targets.length === 1
    const progress: ProgressController = toast.progress(
      single
        ? t('driveView.toast.deleting', { name: targets[0].name })
        : t('driveView.toast.deletingMany', { count: targets.length }),
      single ? 0 : targets.length,
    )
    let settledCount = 0
    const onSingleProgress = (p: DeleteJobProgress) => {
      // A row-level phase has a real count; the rest are one statement each.
      if (shown()) progress.update(p.total > 0 ? p.done : 0, phaseText(p, targets[0].name), p.total > 0 ? p.total : 0)
    }
    const running: Promise<DeleteOutcome>[] = []
    for (const target of targets) {
      const { finished } = await startOne(target, single ? onSingleProgress : undefined, batch)
      running.push(finished.then((o) => {
        if (o.ok) optsRef.current.onDeleted?.(o.target, o.result, at)
        ++settledCount
        if (!single && shown()) progress.update(settledCount)
        return o
      }))
    }
    const outcomes = await Promise.all(running)
    if (!shown()) return outcomes

    const succeeded = outcomes.filter((o): o is Extract<DeleteOutcome, { ok: true }> => o.ok)
    const failed = outcomes.filter((o): o is Extract<DeleteOutcome, { ok: false }> => !o.ok)
    // #801: a split (or a delete that binned nothing) says so, per folder;
    // the whole deletes share one "Deleted …" sentence.
    const splitNotes: string[] = []
    const ok: typeof succeeded = []
    for (const o of succeeded) {
      const note = deleteResultMessage(t, o.result)
      if (note) splitNotes.push(single ? note : `${o.target.name}: ${note}`)
      else ok.push(o)
    }
    const wholeText = ok.length === 0 ? null
      : ok.length > 1
        ? t('driveView.toast.deleted', { name: ok[0].target.name, more: ok.length - 1 })
        : ok[0].result.deletedInSource
          ? t('driveView.toast.deletedInSource', { name: ok[0].target.name })
          : t('driveView.toast.deletedOne', { name: ok[0].target.name })
    if (failed.length === 0) {
      progress.complete(wholeText ?? splitNotes.shift())
    } else {
      progress.fail(failed.length > 1
        ? t('driveView.toast.deleteFailedMany', { count: failed.length, reason: failed[0].message })
        : failed[0].message)
      if (wholeText) toast.success(wholeText)
    }
    for (const note of splitNotes) toast.info(note)
    optsRef.current.onSettled?.()
    return outcomes
  }, [startOne, toast, t, phaseText])

  // ── Resume jobs a reload interrupted ──────────────────────────────────
  useEffect(() => {
    const pending = pendingDeletes.list().filter(p => !following.has(p.jobId))
    if (pending.length === 0) return
    for (const p of pending) {
      const progress = toast.progress(t('driveView.toast.deleting', { name: p.name }), 0)
      // A distinct own-id: the tracker then asks the status route once at the
      // start, since the outcome may have gone out while the page was away.
      const tracker = createDeleteTracker(deps(), `resume-${p.jobId}`)
      void followJob(p, tracker, (x) =>
        progress.update(x.total > 0 ? x.done : 0, phaseText(x, p.name), x.total > 0 ? x.total : 0),
      ).then(
        (result) => {
          optsRef.current.onDeleted?.({ id: p.fileId, name: p.name }, result)
          progress.complete(deleteResultMessage(t, result) ?? (result.deletedInSource
            ? t('driveView.toast.deletedInSource', { name: p.name })
            : t('driveView.toast.deletedOne', { name: p.name })))
        },
        (err) => progress.fail(failureText(err, p.name)),
      ).finally(() => optsRef.current.onSettled?.())
    }
    // Once per mount: a reload is the only way to lose a job this tab started.
  }, [])

  return { deleteItems }
}
